import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@tremor/react";
import * as sourcesAPI from "../../../services/sources";
import { SOURCE_LIST } from "../../sources/utils";

const providerLabel = (provider) => (SOURCE_LIST.find((p) => p.value === provider) || {}).name || provider;

export default function SourcePicker({ value, onChange }) {
  const [sources, setSources] = useState([]);
  const [status, setStatus] = useState("loading"); // loading | ready | error
  const isMounted = useRef(true);

  const load = useCallback(async () => {
    setStatus((s) => (s === "ready" ? s : "loading"));
    try {
      const response = await sourcesAPI.list(1, 100);
      const next = (response && response.data) || [];
      if (isMounted.current) {
        setSources(next);
        setStatus("ready");
        if (value && !next.some((s) => s._id === value)) onChange("");
      }
    } catch (err) {
      if (isMounted.current) {
        setStatus("error");
      }
    }
  }, [value, onChange]);

  // Load once on mount; Refresh and Retry call load() explicitly.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    isMounted.current = true;
    load();
    return () => {
      isMounted.current = false;
    };
  }, []);

  return (
    <fieldset aria-labelledby="source-picker-legend">
      <div className="flex items-baseline justify-between">
        <legend id="source-picker-legend" className="font-medium text-gray-900 dark:text-gray-50">Source</legend>
        <span className="text-sm text-gray-500 dark:text-gray-400">Where your network server sends uplinks</span>
      </div>

      {status === "loading" && <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">Loading sources…</p>}

      {status === "error" && (
        <div className="mt-3 flex items-center gap-3 rounded-lg bg-red-50 dark:bg-red-950/40 px-4 py-3 text-sm text-red-700 dark:text-red-300">
          <span>We couldn't load your sources.</span>
          <Button size="xs" variant="secondary" onClick={load}>Retry</Button>
        </div>
      )}

      {status === "ready" && sources.length === 0 && (
        <div className="mt-3 rounded-lg border border-dashed border-gray-300 dark:border-gray-800 p-5">
          <p className="text-gray-700 dark:text-gray-300">
            <b>You don't have a source yet.</b> A source is the webhook URL your network server
            (TTN, ChirpStack, Helium) posts uplinks to. Create one, then come back — this form keeps what you typed.
          </p>
          <div className="mt-4 flex gap-2">
            <a
              href="/sources"
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-md bg-gray-900 dark:bg-gray-700 px-4 py-2 text-sm font-medium text-white"
            >
              Open Sources ↗
            </a>
            <Button variant="secondary" onClick={load}>Refresh</Button>
          </div>
        </div>
      )}

      {status === "ready" && sources.length > 0 && (
        <div className="mt-3 space-y-3">
          {sources.map((source) => {
            const active = (source.eventCount || 0) > 0;
            return (
              <label
                key={source._id}
                className={`flex cursor-pointer items-center gap-4 rounded-lg border px-4 py-3 ${
                  value === source._id ? "border-blue-600 ring-1 ring-blue-600" : "border-gray-200 dark:border-gray-800"
                }`}
              >
                <input
                  type="radio"
                  name="source"
                  value={source._id}
                  checked={value === source._id}
                  onChange={() => onChange(source._id)}
                  aria-label={source.name}
                />
                <span className="flex-1">
                  <span className="block font-medium text-gray-900 dark:text-gray-50">{source.name}</span>
                  <span className="block text-sm text-gray-500 dark:text-gray-400">
                    {providerLabel(source.provider)} ·{" "}
                    {active ? `${source.eventCount.toLocaleString("en-US")} events` : "no events yet"}
                  </span>
                </span>
                <span
                  className={`rounded-full px-3 py-1 text-xs font-medium ${
                    active ? "bg-green-100 dark:bg-green-950/40 text-green-800 dark:text-green-300" : "bg-amber-100 dark:bg-amber-950/40 text-amber-800 dark:text-amber-300"
                  }`}
                >
                  {active ? "Active" : "No events"}
                </span>
              </label>
            );
          })}
          <div className="text-right">
            <Button size="xs" variant="light" onClick={load}>Refresh</Button>
          </div>
        </div>
      )}
    </fieldset>
  );
}
